import { FC, FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { PlusIcon, MagnifyingGlassIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Layout } from '@components';
import { getAdmins, inviteAdmin } from '@services/admin.service';
import { AdminUser, AdminRole } from '@utilities/types';

const ROLE_LABELS: Record<AdminRole, string> = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    collaborateur: 'Collaborateur',
};

const ROLE_BADGE_CLASSES: Record<AdminRole, string> = {
    super_admin: 'badge-primary',
    admin: 'badge-secondary',
    collaborateur: 'badge-accent',
};

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export const Users: FC = () => {
    const [admins, setAdmins] = useState<AdminUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Pagination & search
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Invite modal state
    const [showModal, setShowModal] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState<AdminRole>('collaborateur');
    const [inviting, setInviting] = useState(false);
    const [inviteError, setInviteError] = useState<string | null>(null);
    const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

    const fetchAdmins = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getAdmins();
            setAdmins(res.data);
        } catch (err: any) {
            setError(err.message || 'Erreur lors du chargement des utilisateurs');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAdmins();
    }, [fetchAdmins]);

    // Filtered admins
    const filtered = useMemo(() => {
        if (!search.trim()) return admins;
        const q = search.toLowerCase();
        return admins.filter((a) => {
            const email = a.email?.toLowerCase() || '';
            const firstName = a.firstName?.toLowerCase() || '';
            const lastName = a.lastName?.toLowerCase() || '';
            const fullName = `${firstName} ${lastName}`;
            const role = ROLE_LABELS[a.role]?.toLowerCase() || a.role;
            return email.includes(q) || firstName.includes(q) || lastName.includes(q) || fullName.includes(q) || role.includes(q);
        });
    }, [admins, search]);

    // Reset page on search/rowsPerPage change
    useEffect(() => { setPage(1); }, [search, rowsPerPage]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
    const paginated = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    const handleInvite = useCallback(async (e: FormEvent) => {
        e.preventDefault();
        setInviting(true);
        setInviteError(null);
        setInviteSuccess(null);

        try {
            await inviteAdmin(inviteEmail, inviteRole);
            setInviteSuccess(`Invitation envoyée à ${inviteEmail}`);
            setInviteEmail('');
            setInviteRole('collaborateur');
            setShowModal(false);
            fetchAdmins();
        } catch (err: any) {
            setInviteError(err.message || "Erreur lors de l'envoi de l'invitation");
        } finally {
            setInviting(false);
        }
    }, [inviteEmail, inviteRole, fetchAdmins]);

    const handleCloseModal = useCallback(() => {
        setShowModal(false);
        setInviteEmail('');
        setInviteRole('collaborateur');
        setInviteError(null);
    }, []);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <span className="loading loading-spinner loading-lg"></span>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold">Utilisateurs</h1>
                    <div className="flex items-center gap-3">
                        <span className="text-sm text-base-content/50">{filtered.length} utilisateur{filtered.length !== 1 ? 's' : ''}</span>
                        <button
                            onClick={() => setShowModal(true)}
                            className="btn btn-primary btn-sm gap-2"
                        >
                            <PlusIcon className="w-4 h-4" />
                            Inviter
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-error mb-4">
                        <span>{error}</span>
                    </div>
                )}

                {inviteSuccess && (
                    <div className="alert alert-success mb-4">
                        <span>{inviteSuccess}</span>
                    </div>
                )}

                {/* Barre de recherche + lignes par page */}
                <div className="flex items-center justify-between gap-4 mb-4">
                    <div className="relative flex-1 max-w-sm">
                        <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
                        <input
                            type="text"
                            placeholder="Rechercher..."
                            className="input input-bordered input-sm w-full pl-9"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-base-content/50">Lignes :</span>
                        <select
                            className="select select-bordered select-sm"
                            value={rowsPerPage}
                            onChange={(e) => setRowsPerPage(Number(e.target.value))}
                        >
                            {ROWS_PER_PAGE_OPTIONS.map((n) => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="overflow-x-auto bg-white rounded-lg border border-base-300">
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Prénom</th>
                                <th>Nom</th>
                                <th>Email</th>
                                <th>Rôle</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginated.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="text-center text-base-content/50 py-8">
                                        Aucun utilisateur trouvé
                                    </td>
                                </tr>
                            ) : (
                                paginated.map((admin) => (
                                    <tr key={admin.id}>
                                        <td>{admin.firstName || '-'}</td>
                                        <td>{admin.lastName || '-'}</td>
                                        <td>{admin.email}</td>
                                        <td>
                                            <span className={`badge ${ROLE_BADGE_CLASSES[admin.role]} badge-sm`}>
                                                {ROLE_LABELS[admin.role]}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                        <span className="text-sm text-base-content/50">
                            {(page - 1) * rowsPerPage + 1}–{Math.min(page * rowsPerPage, filtered.length)} sur {filtered.length}
                        </span>
                        <div className="flex items-center gap-1">
                            <button
                                className="btn btn-ghost btn-sm"
                                disabled={page <= 1}
                                onClick={() => setPage(page - 1)}
                            >
                                <ChevronLeftIcon className="w-4 h-4" />
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                                .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                                    if (idx > 0 && p - (arr[idx - 1]) > 1) acc.push('ellipsis');
                                    acc.push(p);
                                    return acc;
                                }, [])
                                .map((p, idx) =>
                                    p === 'ellipsis' ? (
                                        <span key={`e-${idx}`} className="px-1 text-base-content/30">…</span>
                                    ) : (
                                        <button
                                            key={p}
                                            className={`btn btn-sm ${page === p ? 'btn-primary' : 'btn-ghost'}`}
                                            onClick={() => setPage(p)}
                                        >
                                            {p}
                                        </button>
                                    )
                                )}
                            <button
                                className="btn btn-ghost btn-sm"
                                disabled={page >= totalPages}
                                onClick={() => setPage(page + 1)}
                            >
                                <ChevronRightIcon className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Invite Modal */}
            <dialog className={`modal ${showModal ? 'modal-open' : ''}`}>
                <div className="modal-box">
                    <h3 className="font-bold text-lg mb-4">Inviter un utilisateur</h3>

                    {inviteError && (
                        <div className="alert alert-error mb-4">
                            <span>{inviteError}</span>
                        </div>
                    )}

                    <form onSubmit={handleInvite}>
                        <div className="form-control w-full mb-4">
                            <label className="label">
                                <span className="label-text">Adresse email</span>
                            </label>
                            <input
                                type="email"
                                placeholder="email@exemple.com"
                                className="input input-bordered w-full"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                required
                                disabled={inviting}
                            />
                        </div>

                        <div className="form-control w-full mb-6">
                            <label className="label">
                                <span className="label-text">Rôle</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={inviteRole}
                                onChange={(e) => setInviteRole(e.target.value as AdminRole)}
                                disabled={inviting}
                            >
                                <option value="collaborateur">Collaborateur</option>
                                <option value="admin">Admin</option>
                                <option value="super_admin">Super Admin</option>
                            </select>
                        </div>

                        <div className="modal-action">
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={handleCloseModal}
                                disabled={inviting}
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={inviting}
                            >
                                {inviting ? (
                                    <span className="loading loading-spinner loading-sm"></span>
                                ) : (
                                    'Envoyer l\'invitation'
                                )}
                            </button>
                        </div>
                    </form>
                </div>
                <form method="dialog" className="modal-backdrop">
                    <button onClick={handleCloseModal}>close</button>
                </form>
            </dialog>
        </Layout>
    );
};
